import { Check, Plus } from "lucide-react"

import { monto } from "@/componentes/tesoreria/dinero"

/** Un servicio del catálogo del proveedor, tal como lo necesita la orden. */
export type ServicioOfrecido = {
  id: number
  nombre: string
  monto_referencia: string | null
  divisa: string | null
}

/**
 * Lo que ofrece el proveedor elegido y a qué precio, A LA VISTA (04/11, pedido del
 * usuario): al elegir el proveedor de la orden se ven sus servicios sin abrir ningún
 * desplegable, y uno se añade como línea con un toque.
 *
 * ⚠ **Es el MISMO catálogo que «Gastos y categorías» y que la ficha del proveedor**
 * (`gastos` con su `proveedor_id` y su `monto_referencia`): un precio que se corrige allí
 * se ve aquí. El precio es una REFERENCIA: la línea nace con él y se edita con lo que
 * diga la factura.
 *
 * ⚠ **Pulsar dos veces añade dos líneas**, igual que el desplegable: un servicio puede ir
 * dos veces en la misma factura con otra cantidad. Por eso dice cuántas lleva ya.
 */
export function ServiciosParaAnadir({
  servicios,
  enLineas,
  onAnadir,
}: {
  servicios: ServicioOfrecido[]
  /** Cuántas líneas de la orden salen ya de cada servicio, por su id. */
  enLineas: Map<number, number>
  onAnadir: (servicio: ServicioOfrecido) => void
}) {
  if (servicios.length === 0) return null
  return (
    <ul aria-label="Servicios del proveedor" className="grid gap-2 sm:grid-cols-2">
      {servicios.map((servicio) => {
        const veces = enLineas.get(servicio.id) ?? 0
        return (
          <li key={servicio.id}>
            <button
              type="button"
              onClick={() => onAnadir(servicio)}
              aria-label={`Añadir ${servicio.nombre}`}
              className="tarjeta-interactiva foco-cristal flex min-h-11 w-full items-center gap-3 p-3 text-left"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium break-words">{servicio.nombre}</span>
                <span className="block text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                  {servicio.monto_referencia
                    ? `${monto(servicio.monto_referencia)} ${servicio.divisa ?? ""}`
                    : "Sin precio de referencia"}
                </span>
              </span>
              <span
                className="inline-flex shrink-0 items-center gap-1 text-xs"
                style={{ color: veces > 0 ? "var(--success)" : "var(--fg-muted)" }}
              >
                {veces > 0 ? (
                  <>
                    <Check size={14} aria-hidden={true} />
                    {veces > 1 ? `Añadido ×${veces}` : "Añadido"}
                  </>
                ) : (
                  <>
                    <Plus size={14} aria-hidden={true} /> Añadir
                  </>
                )}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
